'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import AppStoreLink from '@/app/components/AppStoreLink'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Beer and Wine: What Every Drinker Should Know"
      author="MicroPlastics Team"
      date="July 3, 2025"
      readTime="9 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app helping check product safety"
    >
      <BlogTLDR>
        Microplastics have been detected in{' '}
        <strong>all 24 German beer brands tested</strong> in one study and in
        most wine samples studied to date. Sources include barley grown in
        plastic-polluted soils, water used in brewing, plastic-lined fermenters
        and tanks, and (especially) <strong>plastic bottle closures and screw
        caps</strong>. Glass-bottled beer with cork or crown-cap closure is the
        cleanest format; boxed wine and plastic-bottled wine are the worst.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastics detected in 100% of 24 German beer brands tested in one published study.</>,
          <>Worst formats: boxed wine, plastic-bottled wine, plastic-lined kegs, screw-cap plastic seals.</>,
          <>Cleanest formats: glass-bottled beer with crown cap, cork-closed wine.</>,
          <>2026: beer in glass tests high (~100 particles/L) because of painted crown caps; <strong>wine stays low even in glass</strong>, because corks avoid that pathway entirely (ANSES).</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Your Favorite Drink May Come With a Side of Plastic
      </h2>
      <p>
        For millions of people, a glass of beer or wine is one of life&apos;s simple pleasures. Whether
        it is a cold lager after work, a craft IPA on the weekend, or a glass of red wine with dinner,
        alcoholic beverages are deeply woven into social life and culture. But an emerging body of
        scientific research has revealed something unsettling: the beer and wine you drink almost
        certainly contain microplastics.
      </p>
      <p>
        A groundbreaking German study analyzed <strong>24 different beer brands</strong> and found
        microplastic particles in every single one of them. Separate research on wine has found similar
        contamination, with levels varying based on the type of closure, filtration method, and
        packaging material used. These findings have raised important questions about how plastic
        particles enter our beverages and what, if anything, drinkers can do to reduce their exposure.
      </p>
      <p>
        This guide breaks down everything the science tells us about microplastics in alcoholic
        beverages, from how contamination occurs during production to which choices at the store can
        help you minimize your intake.
      </p>

      {/* The German Beer Study */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why Bottled Beer Tests Worse Than Wine: The Cap Effect
      </h2>
      <p>
        A study from ANSES, the French food safety agency, published in the{' '}
        <em>Journal of Food Composition and Analysis</em>, produced the most useful
        explanation yet for a pattern that had puzzled this field: beer consistently tests
        higher for microplastics than wine, even when both come in glass.
      </p>
      <p>
        Across drinks in <strong>glass</strong> bottles -- cola, lemonade, iced tea and beer
        -- researchers measured <strong>five to fifty times more</strong> microplastic
        fragments than in the plastic-bottled equivalents, landing near{' '}
        <strong>100 particles per litre</strong>. The source was traced to the{' '}
        <strong>painted metal cap</strong>: fine scratches in the paint, created by caps
        rubbing together during storage before bottling, shed fragments that fall into the
        drink when it is opened.
      </p>
      <p>
        That mechanism explains the wine result elegantly. <strong>Wine contained few
        microplastics even in glass bottles</strong>, and the obvious structural difference
        is the closure: a cork is not a painted crown cap. The contamination route that
        dominates in beer simply is not present in a corked bottle.
      </p>
      <p>
        Practical reading: the container material matters less than the closure. Draught
        beer and corked wine avoid the crown-cap pathway entirely. And as the researchers
        stressed, with no established reference level for a harmful ingested dose, none of
        these figures can currently be translated into a health risk.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The German Beer Study: Microplastics in Every Brand Tested
      </h2>
      <p>
        The study that first brought widespread attention to microplastics in beer was conducted by
        researchers at the University of Muenster in Germany and published in the journal{' '}
        <em>Food Additives &amp; Contaminants</em>. The team analyzed 24 German beer brands, including
        popular pilsners, wheat beers, and lagers, sourced from ten different breweries. Using
        micro-Raman spectroscopy, they identified and counted microplastic particles in each sample.
      </p>
      <p>
        The results were striking: <strong>every single beer sample contained microplastics</strong>.
        The concentration ranged from <strong>2 to 79 microplastic particles per liter</strong>, with
        an average of approximately <strong>4 particles per liter</strong> across all samples. The most
        commonly identified polymers were polyethylene terephthalate (PET), polyethylene (PE), and
        polypropylene (PP) -- the same plastics found in bottles, packaging, and industrial equipment.
      </p>
      <p>
        A follow-up study conducted by researchers at the State University of New York at Fredonia
        expanded the scope globally, testing <strong>159 samples of beer</strong> from multiple
        countries. They confirmed the German findings and reported an average of{' '}
        <strong>4.05 particles per liter</strong> in beer samples worldwide, with some brands
        containing significantly higher concentrations. Notably, beers packaged in glass bottles, cans,
        and plastic bottles all contained microplastics, indicating that the contamination is not solely
        a packaging issue.
      </p>

      {/* How Microplastics Enter Beer */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Get Into Beer
      </h2>
      <p>
        Understanding how microplastics end up in your pint glass requires looking at the entire brewing
        process, from raw ingredients to the final pour. There are multiple entry points, and most of
        them have nothing to do with the container you drink from.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Water Supply
      </h3>
      <p>
        Beer is approximately 90 to 95 percent water, making the water supply the single largest
        potential source of microplastic contamination. Municipal tap water has been found to contain
        an average of <strong>5.45 microplastic particles per liter</strong> in studies conducted across
        multiple countries. Even when breweries use filtration systems, not all filters are capable of
        removing particles smaller than 10 micrometers, and nanoplastics can pass through most
        conventional filtration systems entirely. For a deeper dive into water contamination, see our
        guide on{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in tap water
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Filtration Equipment
      </h3>
      <p>
        Ironically, the filtration process itself can introduce microplastics into beer. Many breweries
        use diatomaceous earth filters or synthetic polymer-based filter sheets to clarify their
        products. These filters can shed tiny plastic fibers and fragments directly into the beer as
        it passes through. Cross-flow membrane filtration systems, which use polymer membranes, are
        another potential source. Research has found that the type and age of filtration media used by
        a brewery can significantly impact the final microplastic count in the finished product.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Raw Ingredients
      </h3>
      <p>
        Barley, hops, and yeast can all carry microplastic contamination from agricultural environments.
        Crops grown in fields treated with sewage sludge (biosolids) as fertilizer have been shown to
        absorb microplastics from the soil. Plastic mulch films used in hop farming can degrade and
        fragment, leaving microplastic residues on the crop. While the malting and boiling stages of
        brewing may remove some larger particles, smaller fragments and fibers can persist through
        the entire process.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Packaging and Distribution
      </h3>
      <p>
        The final packaging step introduces additional contamination risk. Plastic-lined bottle caps,
        synthetic gaskets in canning equipment, and plastic kegs all have the potential to shed
        microplastics into the beverage. Even glass bottles are not immune, as the plastic seals and
        liners in crown caps can release particles over time, especially under the slight pressure of
        carbonated beer.
      </p>

      {/* Microplastics in Wine */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Microplastics in Wine: Cork vs Screw Cap and Beyond
      </h2>
      <p>
        Wine has received less research attention than beer, but the studies that do exist have found
        comparable levels of contamination. A 2022 study published in <em>Science of the Total
        Environment</em> analyzed wines from multiple European producers and detected microplastic
        particles in every sample tested, with concentrations ranging from{' '}
        <strong>2.1 to 8.3 particles per liter</strong>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        The Cork vs Screw Cap Debate
      </h3>
      <p>
        One of the more interesting findings in wine research relates to closures. Natural cork
        closures, which have been used for centuries, do not introduce significant microplastic
        contamination. They are made from the bark of cork oak trees and contain no synthetic polymers.
        Screw caps, on the other hand, contain a plastic or composite liner that seals against the
        bottle rim. Studies have found that wines sealed with screw caps tend to have{' '}
        <strong>slightly higher microplastic levels</strong> than those sealed with natural cork,
        likely due to degradation of the plastic liner over time.
      </p>
      <p>
        Synthetic cork closures, which are made entirely from plastic polymers, present the highest
        closure-related risk. These closures can shed microplastic particles into the wine, particularly
        during insertion and removal. If minimizing microplastic exposure is a priority, wines sealed
        with natural cork are the preferable option.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Wine Production and Filtration
      </h3>
      <p>
        Similar to beer, the winemaking process itself introduces microplastics at multiple stages.
        Wine is commonly stored and fermented in stainless steel tanks lined with epoxy coatings, or
        in plastic tanks and containers. The fining and filtration process, which clarifies the wine
        and removes sediment, often uses synthetic materials. Bentonite clay fining is generally
        plastic-free, but some producers use polyvinylpolypyrrolidone (PVPP), a synthetic polymer,
        to reduce bitterness and astringency. This polymer can leave trace residues in the final
        product.
      </p>
      <p>
        Wines that undergo minimal filtration, such as many natural and biodynamic wines, may actually
        contain fewer processing-related microplastics, though they may carry higher levels of
        particles from the vineyard environment itself. Unfiltered wines represent a trade-off that
        has not yet been fully studied.
      </p>

      {/* Contamination Levels Compared */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Contamination Levels: How Alcoholic Beverages Compare
      </h2>
      <p>
        When comparing microplastic levels across different alcoholic beverages, the data reveals some
        notable differences. Based on published research, here is how common drinks stack up:
      </p>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Beer:</strong> Average of <strong>4 to 5 particles per liter</strong>, with some
          samples reaching up to 79 particles per liter. Glass-bottled, canned, and draft beer all
          show contamination, with draft beer from kegs sometimes showing slightly lower levels.
        </li>
        <li>
          <strong>Wine:</strong> Average of <strong>2 to 8 particles per liter</strong>, varying
          based on closure type, filtration method, and packaging. Red wines and white wines show
          similar levels, though individual bottles can vary considerably.
        </li>
        <li>
          <strong>Spirits:</strong> Limited research exists, but preliminary studies suggest that
          distilled spirits may contain <strong>fewer microplastics</strong> than beer or wine. The
          distillation process, which involves vaporizing and recondensing the alcohol, may leave
          behind many solid contaminants including microplastics. However, contamination can still
          occur during post-distillation storage and bottling.
        </li>
        <li>
          <strong>Cider:</strong> Studies have found microplastic levels in cider comparable to or
          slightly lower than beer, averaging around <strong>3 to 4 particles per liter</strong>.
          Like wine, the closure type and filtration method play a significant role.
        </li>
      </ul>
      <p>
        For context, the average person who drinks two beers per day would ingest approximately{' '}
        <strong>3,000 to 4,000 additional microplastic particles per year</strong> from beer alone.
        While this is a relatively small fraction of the estimated 74,000 to 121,000 particles
        ingested annually from all dietary sources, it is an avoidable source that can be reduced
        with informed choices.
      </p>

      {/* Glass vs Can vs Plastic */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Glass vs Can vs Plastic Bottle: Does Packaging Matter?
      </h2>
      <p>
        Many consumers assume that choosing glass over plastic automatically means fewer microplastics.
        The reality is more nuanced than that.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Glass Bottles
      </h3>
      <p>
        Glass itself does not shed microplastics, making it the safest container material. However,
        the crown cap or closure still contains plastic liners, and beer or wine stored in glass still
        carries whatever contamination was introduced during production. Glass bottles do have the
        advantage of not adding to the microplastic load after packaging, which gives them an edge over
        the alternatives.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Aluminum Cans
      </h3>
      <p>
        Aluminum cans are lined with a thin layer of epoxy resin, which often contains bisphenol A
        (BPA) or its substitutes. While the primary concern with cans is chemical leaching rather than
        microplastic shedding, the epoxy lining can degrade over time and release micro-particles.
        Studies have found that canned beverages contain slightly higher levels of certain plastic-
        associated chemicals than their glass-bottled counterparts, though the microplastic particle
        count difference is not dramatic.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Plastic Bottles
      </h3>
      <p>
        Beer and wine sold in PET plastic bottles consistently show the highest microplastic levels.
        Research on{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in bottled water
        </Link>{' '}
        has demonstrated that PET bottles can shed thousands of particles per liter, and the same
        principle applies to alcoholic beverages stored in plastic. Heat exposure during transport
        and storage accelerates this shedding. If you have the option, avoiding plastic-bottled
        alcohol is one of the simplest ways to reduce your exposure.
      </p>

      {/* Craft Beer vs Mass-Produced */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Craft Beer vs Mass-Produced: Is There a Difference?
      </h2>
      <p>
        The question of whether craft beer is cleaner than mass-produced beer does not have a simple
        answer, but there are factors that may work in craft beer&apos;s favor.
      </p>
      <p>
        Large-scale commercial breweries process enormous volumes of beer through extensive industrial
        filtration systems. These systems use synthetic filter media that can introduce microplastics
        at scale. The beer may also travel through long networks of plastic tubing and be stored in
        large plastic-lined tanks before packaging.
      </p>
      <p>
        Craft breweries, particularly smaller operations, often use shorter production chains with less
        equipment contact. Some craft brewers deliberately produce unfiltered or lightly filtered beers,
        which may reduce filtration-related microplastic introduction. However, craft breweries may
        also use less sophisticated water treatment, and their smaller scale does not guarantee lower
        contamination. The limited research comparing the two suggests that{' '}
        <strong>unfiltered craft beers may contain fewer processing-derived microplastics</strong>,
        but the difference is not yet well quantified.
      </p>
      <p>
        Draft beer served directly from kegs in bars and restaurants may also have an advantage over
        bottled beer, as the stainless steel kegs and dispensing systems introduce fewer plastic
        contact points than bottling lines. However, plastic beer lines connecting kegs to taps are
        standard in most establishments, and these lines are replaced infrequently, potentially
        shedding microplastics into every pour.
      </p>

      {/* Tips for Reducing Exposure */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Tips for Reducing Microplastic Exposure From Alcohol
      </h2>
      <p>
        While you cannot eliminate microplastic exposure from alcoholic beverages entirely, the
        following strategies can help you minimize it:
      </p>
      <ol className="list-decimal pl-6 space-y-3">
        <li>
          <strong>Choose glass bottles over cans and plastic.</strong> Glass does not shed
          microplastics. While the contents may still contain particles from production, you avoid
          the additional contamination from plastic packaging.
        </li>
        <li>
          <strong>Opt for natural cork closures in wine.</strong> If you are choosing between two
          similar wines, the one with a natural cork closure is likely to have fewer microplastics
          than one with a screw cap or synthetic cork.
        </li>
        <li>
          <strong>Consider unfiltered beers and wines.</strong> Products that skip heavy filtration
          may avoid the microplastics introduced by synthetic filter media. Look for labels that
          say &ldquo;unfiltered,&rdquo; &ldquo;bottle-conditioned,&rdquo; or &ldquo;naturally
          cloudy.&rdquo;
        </li>
        <li>
          <strong>Store alcohol properly.</strong> Keep bottles and cans away from heat and direct
          sunlight. High temperatures accelerate the breakdown of plastic liners and closures,
          increasing microplastic release into the beverage.
        </li>
        <li>
          <strong>Avoid plastic cups and stirrers.</strong> When drinking at home or at events,
          use glass, ceramic, or stainless steel drinkware instead of plastic cups. Plastic straws,
          stirrers, and disposable cups add directly to your microplastic intake.
        </li>
        <li>
          <strong>Moderate your consumption.</strong> Beyond the well-known health benefits of
          moderate drinking, reducing the volume of alcohol you consume directly reduces the number
          of microplastic particles you ingest from this source.
        </li>
        <li>
          <strong>Support breweries and wineries that prioritize clean production.</strong> As
          awareness grows, some producers are beginning to invest in microplastic-free filtration
          systems and non-plastic production equipment. Supporting these producers sends a market
          signal that consumers care about this issue.
        </li>
      </ol>

      {/* FAQ Section */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Frequently Asked Questions
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        How many microplastics are in a typical beer?
      </h3>
      <p>
        Research has found an average of approximately 4 to 5 microplastic particles per liter of beer.
        A standard 12-ounce (355 ml) serving would contain roughly 1 to 2 particles on average, though
        individual beers can vary significantly. Some samples in the German study contained up to 79
        particles per liter, which would mean approximately 28 particles in a single liter bottle.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Is wine safer than beer for microplastics?
      </h3>
      <p>
        Wine and beer show similar contamination levels, typically ranging from 2 to 8 particles per
        liter. Neither is dramatically safer than the other. The closure type and production method
        matter more than the beverage category. Wine with natural cork closures may have a slight
        advantage over canned beer or beer with plastic-lined caps, but the differences are modest.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Do microplastics in alcohol pose a health risk?
      </h3>
      <p>
        The health effects of ingesting microplastics at the levels found in alcoholic beverages are
        still being studied. The quantities from alcohol alone are relatively small compared to total
        dietary exposure. However, microplastics can carry harmful chemicals such as phthalates,
        BPA, and heavy metals, and they contribute to your cumulative body burden. Reducing exposure
        from all sources, including alcohol, is a prudent approach based on the precautionary
        principle. For more on health impacts, see our article on{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastic health effects
        </Link>.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Does the brewing or distillation process remove microplastics?
      </h3>
      <p>
        Distillation is more effective than brewing at reducing microplastics because it involves
        vaporizing the liquid and recondensing it, which can leave solid particles behind. Brewing
        does not include this step, so microplastics present in the water and ingredients tend to
        remain in the final product. Filtration during brewing can both remove and introduce
        microplastics depending on the filter materials used.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Are organic or natural wines better for avoiding microplastics?
      </h3>
      <p>
        Organic certification primarily addresses pesticide and chemical use, not microplastic
        contamination. However, natural and biodynamic wines that use minimal filtration and avoid
        synthetic fining agents like PVPP may contain fewer processing-related microplastics. The
        trade-off is that they may contain more environmental particles from the vineyard. The
        research on this specific question is still limited.
      </p>

      {/* App CTA */}
      <div className="bg-gradient-to-r from-[#1E6B47]/20 to-[#1E6B47]/5 rounded-xl p-6 mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-2">Check Your Products with the MicroPlastics App</h3>
        <p className="text-gray-600 mb-4">Scan any product to instantly see its microplastic risk level. Start with 3 free scans.</p>
        <AppStoreLink source="blog_post_inline" className="inline-flex items-center gap-2 bg-[#1E6B47] text-white font-semibold px-6 py-3 rounded-control hover:bg-[#f0a060] transition-colors pressable">Download Free on iOS</AppStoreLink>
      </div>

      {/* Related Articles */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Related Articles
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Food: Which Foods Contain the Most?
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Bottled Water: Why You Should Switch to Filtered Tap
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-in-tap-water" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Tap Water: Is Your Drinking Water Safe?
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
            The Health Effects of Microplastics: What Science Says
          </Link>
        </li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Does beer contain microplastics?',
            answer:
              'Yes. A German study tested 24 beer brands and found microplastics in 100% of samples. Sources include plastic-lined brewery equipment, water used in brewing, and (significantly) plastic-cap inner liners on bottles.',
          },
          {
            question: 'Which is cleaner: beer or wine?',
            answer:
              'Format matters more than the beverage. Glass-bottled beer with a crown cap or cork-closed wine in a glass bottle are similar low-risk. Boxed wine (Tetrapak or bag-in-box with PE/EVOH layers), plastic-bottled wine, and any beverage in a plastic-lined screw cap are noticeably worse.',
          },
          {
            question: 'Should I avoid boxed wine?',
            answer:
              'For microplastics, yes, boxed wine sits in direct contact with multi-layer plastic film for months, leaching particles continuously. For cost, sustainability, and reducing single-glass oxidation, boxed wine has tradeoffs. If you choose box, drink it within days of opening.',
          },
          {
            question: 'Does decanting wine remove microplastics?',
            answer:
              'No. Particles stay suspended; no consumer-grade filter at home removes them effectively. Choose better source packaging instead of trying to remove them after.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'ANSES (French Agency for Food, Environmental and Occupational Health Safety)',
            year: 2025,
            title: 'The caps of glass bottles contaminate beverages with microplastics',
            publisher: 'Journal of Food Composition and Analysis',
            url: 'https://www.anses.fr/en/content/caps-glass-bottles-contaminate-beverages-microplastics',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human Consumption of Microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2017,
            title: 'The presence of microplastics in commercial salts from different countries',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/srep46173',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
